import { adminCacheBus } from '../crossTab/adminCrossTab.js';

interface FailureCacheEntry {
  failedAt: number;
  ttlMs: number;
  status: number;
}

interface RequestFailureWriteOptions {
  /** Apply a remote peer signal — do not rebroadcast (loop guard). */
  readonly fromCrossTab?: boolean;
  /** Explicit TTL from peer; defaults to status-based TTL when omitted. */
  readonly ttlMs?: number;
}

const DEFAULT_FAILURE_TTL_MS = 5 * 60 * 1000;
/** Short TTL — analytics fan-out storms should recover quickly after cool-down. */
const RATE_LIMIT_FAILURE_TTL_MS = 45 * 1000;

const failureCache = new Map<string, FailureCacheEntry>();
let crossTabInitialized = false;

/** Negative / deny-cache: skip repeating known permanent or rate-limited failures. */
export class CachedRequestFailureError extends Error {
  public readonly cacheKey: string;
  public readonly status: number;

  public constructor(cacheKey: string, status: number) {
    super(`Skipping cached failure for ${cacheKey}`);
    this.name = 'CachedRequestFailureError';
    this.cacheKey = cacheKey;
    this.status = status;
  }
}

/** Validation must not stick — query fixes should retry immediately. */
const VALIDATION_FAILURE_TTL_MS = 0;

/**
 * Blips that must not poison Products/Transactions (and other list GETs).
 * 500/503/401/network are common on first click during session settle or a single
 * backend hiccup; caching them defeated retryOnceOnTransientAdminError and made
 * Retry / remount within the old TTL a CachedRequestFailureError no-op.
 * Legacy 503 entries are purged on peek via !shouldCacheFailureStatus.
 */
const TRANSIENT_FAILURE_STATUSES = new Set([0, 401, 408, 425, 500, 502, 503, 504]);

export function isAbortError(error: unknown): boolean {
  return (
    (error instanceof DOMException && error.name === 'AbortError')
    || (error instanceof Error && error.name === 'AbortError')
  );
}

export function isTransientRequestFailureStatus(status: number): boolean {
  return TRANSIENT_FAILURE_STATUSES.has(status);
}

/**
 * Validation / bad-request must not poison the failure cache — clients often
 * fix query params mid-session (e.g. Mission Control tenantId serialization).
 * Transient 5xx (incl. 503) / 401 / network (0) must not stick either — Retry and remount
 * must hit the network. Legacy 400/422/5xx entries are purged on peek.
 */
function shouldCacheFailureStatus(status: number): boolean {
  if (status === 400 || status === 422) {
    return false;
  }
  return !isTransientRequestFailureStatus(status);
}

function ttlForStatus(status: number): number {
  if (!shouldCacheFailureStatus(status)) {
    return VALIDATION_FAILURE_TTL_MS;
  }
  if (status === 429) {
    return RATE_LIMIT_FAILURE_TTL_MS;
  }
  return DEFAULT_FAILURE_TTL_MS;
}

/** Peer-publish only durable deny-cache signals (429). Transient 503 is not cached. */
function shouldPublishRequestFailure(status: number): boolean {
  return status === 429;
}

export function buildRequestFailureCacheKey(parts: readonly string[]): string {
  return parts.join('|');
}

export function peekCachedFailureStatus(key: string): number | undefined {
  const entry = failureCache.get(key);
  if (!entry) {
    return undefined;
  }
  if (Date.now() - entry.failedAt > entry.ttlMs) {
    failureCache.delete(key);
    return undefined;
  }
  if (!shouldCacheFailureStatus(entry.status)) {
    failureCache.delete(key);
    return undefined;
  }
  return entry.status;
}

export function shouldSkipCachedFailure(key: string): boolean {
  return peekCachedFailureStatus(key) !== undefined;
}

export function throwIfCachedFailure(key: string): void {
  const status = peekCachedFailureStatus(key);
  if (status === undefined) {
    return;
  }
  throw new CachedRequestFailureError(key, status);
}

export function markRequestFailure(
  key: string,
  status: number,
  options?: RequestFailureWriteOptions,
): void {
  if (!shouldCacheFailureStatus(status)) {
    failureCache.delete(key);
    return;
  }
  const ttlMs = options?.ttlMs ?? ttlForStatus(status);
  // Skip caching zero-TTL statuses so retries are not blocked.
  if (ttlMs <= 0) {
    failureCache.delete(key);
    return;
  }
  failureCache.set(key, {
    failedAt: Date.now(),
    ttlMs,
    status,
  });
  if (options?.fromCrossTab === true) {
    return;
  }
  if (!shouldPublishRequestFailure(status)) {
    return;
  }
  adminCacheBus.publish({
    type: 'request-failure',
    cacheKey: key,
    status,
    ttlMs,
  });
}

export function clearRequestFailure(key: string): void {
  failureCache.delete(key);
}

export function clearAllRequestFailures(): void {
  failureCache.clear();
}

/**
 * Test-only: write a raw Map entry, bypassing shouldCacheFailureStatus.
 * Used to simulate legacy entries (e.g. status 500) that must be purged on peek.
 */
export function seedRequestFailureEntryForTests(
  key: string,
  status: number,
  ttlMs: number,
): void {
  failureCache.set(key, {
    failedAt: Date.now(),
    ttlMs,
    status,
  });
}

/**
 * Subscribe once to peer-tab 429 failure signals.
 * Safe to call repeatedly; additional calls are no-ops.
 */
export function initRequestFailureCacheCrossTab(): void {
  if (crossTabInitialized) {
    return;
  }
  crossTabInitialized = true;
  adminCacheBus.subscribe((message) => {
    if (message.type !== 'request-failure') {
      return;
    }
    markRequestFailure(message.cacheKey, message.status, {
      fromCrossTab: true,
      ttlMs: message.ttlMs,
    });
  });
}
